import { SOURCES } from './sources';

export const FRUITS = [
  {
    slug: "baer",
    shortTitle: "Bær",
    title: "Kan man fryse bær?",
    metaTitle: "Kan man fryse jordbær, blåbær og hindbær? — Guide",
    metaDescription: "Ja, bær fryser glimrende og holder 10–12 måneder jf. Tænk. Løsfrys dem på en bageplade. Vær opmærksom på kogepåbud for købte hindbær.",
    answer: "Ja, alle bær kan fryses med fantastisk resultat og holder sig i 10–12 måneder på frost, men de mister spændstighed og egner sig bedst til grød, bagværk og smoothies.",
    steps: [
      "Sorter bærrene omhyggeligt og kasser overmodne eller mugne eksemplarer.",
      "Skyl bærrene forsigtigt i koldt vand og lad dem tørre helt på et rent viskestykke.",
      "Løsfrys bærrene i et enkelt lag på en bageplade i 2 timer, før de hældes i fryseposer jf. Arla og Samvirke.",
      "Købte frosne hindbær bør ifølge Fødevarestyrelsen altid koges i mindst 1 minut pga. risiko for norovirus.",
      "Bær fra egen have behøver ikke koges ifølge Fødevarestyrelsen og DTU Fødevareinstituttet."
    ],
    tableNote: "Kilde: Samvirke, Arla og Forbrugerrådet Tænk",
    table: [
      { food: "Blåbær (have- og vilde)", duration: "10–12 måneder", pack: "Løsfrosset i pose", thaw: "Direkte i dejen fra frossen", use: "Frugt holder 10–12 mdr. jf. Tænk; bevarer faconen i bagværk" },
      { food: "Hindbær (købte)", duration: "10–12 måneder", pack: "Løsfrosset i flad boks", thaw: "Koges i mindst 1 minut", use: "Fødevarestyrelsen påbyder 1 min kogning for købte frosne bær" },
      { food: "Hindbær (fra egen have)", duration: "10–12 måneder", pack: "Løsfrosset i flad boks", thaw: "Køleskab eller direkte i ret", use: "Behøver ikke koges ifølge Fødevarestyrelsen jf. Samvirke" },
      { food: "Jordbær", duration: "10–12 måneder", pack: "Fladt i pose jf. Samvirke", thaw: "Køleskab langsomt", use: "Bliver bløde; geniale til grød, smoothies og marmelade" },
      { food: "Ribs og solbær", duration: "10–12 måneder", pack: "Ribbet fra stilk i pose", thaw: "Direkte i gryde", use: "Rysteribs, saft, syltning" },
      { food: "Kirsebær (med eller uden sten)", duration: "10–12 måneder", pack: "Frysepose uden luft", thaw: "Køleskab eller gryde", use: "Kirsebærsauce til risalamande, tærter" },
      { food: "Friske bær i køleskab", duration: "1–2 dage", pack: "Original emballage i køleskab", thaw: "Ikke relevant", use: "Kort holdbarhed; spis hurtigt eller frys jf. Arla og Tænk" }
    ],
    sections: [
      {
        h2: "Fødevarestyrelsens kogepåbud for frosne hindbær",
        body: `Ifølge Samvirkes gennemgang anbefaler Fødevarestyrelsen, at frosne bær fra supermarkedet altid koges i mindst ét minut, hvis de skal spises uden yderligere varmebehandling (fx i smoothies, kold bærcoulis eller som pynt på kager).

Årsagen er, at frosne hindbær i sjældne tilfælde har været forurenet med norovirus (roskildesyge) eller hepatitis A-virus. Vira dør ikke ved frysning, men inaktiveres ved høj varme. Hindbær fra egen have er derimod ikke underlagt samme kogekrav, fordi bærrene ikke har været igennem de mange led og hænder i den industrielle forarbejdning.`
      },
      {
        h2: "Metoden med løsfrysning på plade",
        body: `Arlas bærguide og Samvirke anbefaler begge at vaske bærrene og fryse dem fladt i et enkelt lag på en plade eller bakke med bagepapir.

Når bærrene er frosset faste efter 1–2 timer, hældes de over i en frysepose. Herved undgår du, at bærrene klumper sammen til en uformelig isklump, og du kan nemt dosere præcis den mængde, du skal bruge til havregrød eller bagværk.`
      },
      {
        h2: "Holdbarhed i fryser og køleskab",
        body: `Ifølge Forbrugerrådet Tænks holdbarhedsoversigt kan frugt og frugtprodukter holde sig i 10–12 måneder i fryseren.

I køleskabet har friske bær en meget kort holdbarhed: Jordbær og hindbær holder kun 1–2 dage ifølge Arla og Tænk, mens blåbær kan holde 5–7 dage ved 2–4 grader. De bær, du ikke spiser med det samme, bør derfor fryses ned.`
      }
    ],
    faq: [
      { q: "Hvorfor skal frosne hindbær koges?", a: "Købte frosne hindbær kan indeholde norovirus. Fødevarestyrelsen anbefaler at koge bærrene i mindst ét minut for at inaktivere virussen." },
      { q: "Skal bær fra egen have også koges?", a: "Nej, ifølge Fødevarestyrelsen og DTU Fødevareinstituttet er det ikke nødvendigt at koge bær fra egen have, da de ikke har været igennem industriel håndtering." },
      { q: "Hvor længe kan frosne bær holde sig?", a: "Frugt og frugtprodukter kan holde sig i 10–12 måneder på frost ifølge Forbrugerrådet Tænk." },
      { q: "Hvor længe holder friske bær i køleskabet?", a: "Jordbær og hindbær holder typisk kun 1–2 dage på køl ifølge Arla og Forbrugerrådet Tænk; frys resterne ned i tide." }
    ],
    internalLinks: [
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhedstabel for frugt og madvarer", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af æbler og pærer", href: "/aebler-og-paerer" },
      { text: "Frysning af rabarber", href: "/rabarber" },
      { text: "Frysning af kager og bagværk", href: "/kager" }
    ],
    sources: [
      SOURCES.samvirkeBaer,
      SOURCES.arlaBaer,
      SOURCES.taenkHoldbarhed
    ]
  },
  {
    slug: "aebler-og-paerer",
    shortTitle: "Æbler og pærer",
    title: "Kan man fryse æbler og pærer?",
    metaTitle: "Kan man fryse æbler, pærer og æblemos? — Se råd her",
    metaDescription: "Frys ikke hele rå æbler, da de bliver bløde og melede. Skær i både og vend i citron, eller frys som æblemos og kompot. Læs guiden.",
    answer: "Ja, men ikke hele og rå: Æbler og pærer bør skæres i både og overhældes med citronsaft, eller koges til mos og kompot før frysning.",
    steps: [
      "Frys aldrig hele, ubehandlede æbler eller pærer; de bliver brune, svampede og uappetitlige ved optøning.",
      "Skræl frugten (hvis ønsket), fjern kernehuset, og skær i ensartede både eller tern.",
      "Vend frugtbådene i koldt vand med citronsaft for at modvirke enzymatisk brunfarvning.",
      "Løsfrys bådene på bagepapir i 2 timer, før de kommes i en frysepose.",
      "Kog overskudsfrugt til æblemos eller pærekompot og frys det ned i tætte bøtter."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Fødevarestyrelsen",
    table: [
      { food: "Æblebåde (med citronsaft)", duration: "10–12 måneder", pack: "Løsfrosset i frysepose", thaw: "Direkte på tærtedej eller i gryde", use: "Frugt holder 10–12 mdr. jf. Tænk; til tærter og crumble" },
      { food: "Hjemmelavet æblemos / kompot", duration: "10–12 måneder", pack: "Tætsluttende boks eller pose", thaw: "Køleskab", use: "Gammeldags æblekage, morgengrød" },
      { food: "Pærebåde", duration: "10–12 måneder", pack: "Løsfrosset i frysepose", thaw: "Direkte i kagedej", use: "Frugt holder 10–12 mdr. jf. Tænk; pæretærter" },
      { food: "Hele rå æbler", duration: "Uegnet til hel frys", pack: "Cellerne sprænges", thaw: "Kollapser til melet væske", use: "Bør opbevares køligt/i køleskab uden frys jf. Tænk" }
    ],
    sections: [
      {
        h2: "Hvorfor bliver æbler brune ved frost?",
        body: `Æbler og pærer indeholder enzymer, som ved kontakt med ilt skaber en brunfarvning af frugtkødet. Når cellevæggene sprænges under frysning, accelereres denne iltning.

Tricket til at bevare den lyse farve er citronsaft. Syren (C-vitamin) hæmmer enzymernes aktivitet på overfladen, så frugten forbliver lys og appetitlig på frost.`
      },
      {
        h2: "Æblemos og kompot: Den mest pladsbesparende løsning",
        body: `Har haven givet et stort udbytte af æbler, er æblemos uden sammenligning den bedste måde at fryse frugten på.

Skær æblerne i grove stykker og kog dem møre med en smule vand og sukker. Mos det let og lad mosen køle helt af før indfrysning i flade poser eller bøtter. Frugt og frugtprodukter holder 10–12 måneder på frost ifølge Forbrugerrådet Tænk.`
      },
      {
        h2: "Sådan bruges frosne frugtbåde i bagværk",
        body: `Frosne æblebåde skal kommes direkte på den ubagte kagedej eller tærtebund fra frossen tilstand. Hvis de tør op på forhånd, udskiller de masser af frugtsaft, der kan gøre bunden våd. I ovnen bager de møre og saftige.`
      }
    ],
    faq: [
      { q: "Kan man fryse hele rå æbler?", a: "Nej, hele æbler mister al sprødhed og bliver melede og brune. Skær dem i både eller lav mos." },
      { q: "Hvor længe holder frosne æbler og æblemos?", a: "Frugt og frugtprodukter holder sig i 10–12 måneder i fryseren ifølge Forbrugerrådet Tænk." },
      { q: "Hvor skal friske æbler opbevares?", a: "Ifølge Forbrugerrådet Tænk opbevares friske æbler bedst i køleskabet eller på altanen, hvor der er mørkt og køligt." },
      { q: "Hvor længe holder friske pærer?", a: "Pærer kan holde sig 5–6 dage på køkkenbordet og længere i køleskabet, når de er modne jf. Tænk." }
    ],
    internalLinks: [
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhedstabeller for frugt", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af bær og frugtprodukter", href: "/baer" },
      { text: "Frysning af rabarber", href: "/rabarber" },
      { text: "Frysning af kager og bagværk", href: "/kager" }
    ],
    sources: [
      SOURCES.foedevarestyrelsenOpbevaring,
      SOURCES.taenkHoldbarhed
    ]
  },
  {
    slug: "bananer-avocado-og-citrus",
    shortTitle: "Bananer, avocado og citrus",
    title: "Kan man fryse bananer, avocado og citrusfrugter?",
    metaTitle: "Kan man fryse bananer, avocado og citroner? — Guide",
    metaDescription: "Frys overmodne bananer uden skræl til smoothies og bananbrød. Samvirke anbefaler citronskiver og saft til isterninger. Læs rådene her.",
    answer: "Ja, overmodne bananer uden skræl, moset avocado med citronsaft samt citrussaft og revet skal fryser glimrende, men hele frugter med skræl bør undgås.",
    steps: [
      "Pil altid bananerne før frysning; frosne bananskræller bliver kulsorte og er umulige at fjerne.",
      "Skær bananerne i skiver eller frys dem halve; de blendes fantastisk til 'nicecream' eller bruges i banankage.",
      "Samvirke anbefaler at presse citronsaft og fryse den i en isterningbakke, eller fryse økologiske citronskiver fladt i en pose som 'isterninger'.",
      "Mos moden avocado med 1 spsk. frisk citronsaft for at forhindre iltning og mørkfarvning.",
      "Vandmelon mister al sprødhed og kan kun blendes frossen til forfriskende slushice."
    ],
    tableNote: "Kilde: Samvirke, Fødevarestyrelsen og Forbrugerrådet Tænk",
    table: [
      { food: "Bananer (skrællede, i skiver)", duration: "10–12 måneder", pack: "Frysepose uden luft", thaw: "Blendes direkte fra frost", use: "Frugt holder 10–12 mdr. jf. Tænk; smoothies, bananis ('nicecream')" },
      { food: "Citronsaft (i isterningbakke)", duration: "10–12 måneder", pack: "Isterningbakke jf. Samvirke", thaw: "Direkte i drik eller mad", use: "Samvirke anbefaler citronsaft i isterningbakke til mad og drikke" },
      { food: "Citronskiver (økologiske)", duration: "10–12 måneder", pack: "Frosset fladt i pose jf. Samvirke", thaw: "Direkte i koldt vand", use: "Fungerer som forfriskende isterninger jf. Samvirke" },
      { food: "Avocado (moset med citronsaft)", duration: "Forlænger holdbarhed", pack: "Tæt boks med film mod overfladen", thaw: "Køleskab langsomt", use: "Syren modvirker iltning; velegnet til guacamole og smoothies" },
      { food: "Vandmelon", duration: "Uegnet til frisk spisning", pack: "Cellerne kollapser", thaw: "Bliver til rent vand", use: "Mister sprødhed; kan udelukkende blendes frossen til slushice" }
    ],
    sections: [
      {
        h2: "Samvirkes råd om citroner og citrus",
        body: `Samvirke fremhæver citroner som en af de 10 madrester, man med fordel kan fryse: Er citronerne ved at blive kedelige, kan du presse saften og fryse den i en fødevaregodkendt isterningebakke til senere brug.

Er citronen økologisk, kan du skære den i skiver og fryse skiverne fladt i en pose, så de ikke fryser sammen – så har du forfriskende 'isterninger' til dit vand eller danskvand. I køleskabet kan citrusfrugter holde 3–5 uger ifølge Forbrugerrådet Tænk.`
      },
      {
        h2: "Overmodne bananer til smoothies og is",
        body: `Bananer skal ifølge Forbrugerrådet Tænk opbevares ved stuetemperatur, hvor de holder 4–7 dage; de skal ikke i køleskabet, da kulden giver kuldeskader.

Er bananerne blevet brune og overmodne, er fryseren den ideelle redning. Pil altid skrællen af før frysning. Skær bananen i skiver og frys dem i en frysepose. Frosne bananskiver blendes direkte til en cremet bananis ('nicecream') eller tøs op til bananbrød og pandekager. Frugt på frost holder 10–12 måneder ifølge Tænk.`
      },
      {
        h2: "Avocado med citronsaft",
        body: `Modne avocadoer kan kun holde sig et par dage på køkkenbordet ifølge Forbrugerrådet Tænk, men holdbarheden kan forlænges ved at lægge dem i køleskabet.

Vil du fryse overskydende avocado, skal den moses med en skefuld citronsaft. Citronsyren forhindrer, at enzymerne gør avocadomassen gråbrun og kedelig. Pak mosen helt lufttæt med plastfilm mod overfladen.`
      }
    ],
    faq: [
      { q: "Hvordan fryser man citroner ifølge Samvirke?", a: "Samvirke anbefaler at presse saften i en isterningbakke eller fryse økologiske citronskiver fladt i en pose til vand og drikkevarer." },
      { q: "Må man fryse bananer med skræl?", a: "Nej, skrællen bliver slimet og kulsort på frost og klæber sig fast til frugten. Pil altid bananen før frysning." },
      { q: "Hvor længe holder bananer og citrus ved stuetemperatur?", a: "Bananer holder 4–7 dage, og citrusfrugter holder 2–3 uger ved stuetemperatur ifølge Forbrugerrådet Tænk." },
      { q: "Hvor længe kan frossen frugt holde sig?", a: "Frugt og frugtprodukter kan holde sig i 10–12 måneder i fryseren ifølge Forbrugerrådet Tænks oversigt." }
    ],
    internalLinks: [
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhedstabeller for frugt på frost", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Hvad der ikke tåler frost", href: "/hvad-kan-man-ikke-fryse" },
      { text: "Frysning af bær", href: "/baer" },
      { text: "Frysning af æbler og frugt", href: "/aebler-og-paerer" }
    ],
    sources: [
      SOURCES.samvirkeRester,
      SOURCES.taenkHoldbarhed,
      SOURCES.foedevarestyrelsenOpbevaring
    ]
  },
  {
    slug: "rabarber",
    shortTitle: "Rabarber",
    title: "Kan man fryse rabarber?",
    metaTitle: "Kan man fryse rå rabarber og kompot? — Guide",
    metaDescription: "Ja, rabarber fryser helt fantastisk både rå i stykker og som færdig kompot. Kræver ingen blanchering. Læs holdbarhed og tips her.",
    answer: "Ja, rabarber er en af de nemmeste råvarer at fryse og bevarer sin syrlige smag og flotte farve i 10–12 måneder uden forudgående blanchering.",
    steps: [
      "Skær det grønne blad og den nederste rodende af rabarberstilkene (bladene indeholder giftig oxalsyre og må ikke spises).",
      "Vask stilkene grundigt i koldt vand for at fjerne jordrester og tør dem med et viskestykke.",
      "Skær stilkene i ensartede stykker på 1–2 centimeters længde.",
      "Pak rabarberstykkerne direkte i fryseposer uden blanchering og pres luften ud.",
      "Tilsæt frosne rabarberstykker direkte i kager, tærter, kompot eller saft uden forudgående optøning."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Fødevarestyrelsen",
    table: [
      { food: "Rå rabarber (i stykker)", duration: "10–12 måneder", pack: "Frysepose uden luft", thaw: "Direkte i gryde eller kagedej", use: "Frugt holder 10–12 mdr. jf. Tænk; til crumble, tærter og saft" },
      { food: "Rabarberkompot (kogt med sukker)", duration: "10–12 måneder", pack: "Tætsluttende plastboks/glas", thaw: "Køleskab langsomt", use: "Dessert, tilbehør til kylling og kødretter" },
      { food: "Rabarbersaft (koncentreret)", duration: "10–12 måneder", pack: "Plastflaske med ekspansionsplads", thaw: "Køleskab", use: "Læskedrik opblandet med koldt vand/danskvand" }
    ],
    sections: [
      {
        h2: "Hvorfor behøver rabarber ikke blanchering?",
        body: `Mange grøntsager kræver et kort opkog (blanchering) for at deaktivere enzymer. Rabarber har imidlertid et naturligt højt indhold af oxalsyre og æblesyre, hvilket giver et meget lavt pH-niveau.

Denne naturlige syre fungerer som et effektivt konserveringsmiddel, der standser enzymaktiviteten. Rå rabarberstykker kan derfor fryses direkte ned uden blanchering.`
      },
      {
        h2: "Sådan bruges frosne rabarber i bagværk",
        body: `Når frosne rabarberstykker tør op, udskiller de en del syrlig væske. Læg dem derfor ikke til optøning før bagning.

Tag i stedet rabarberstykkerne direkte fra fryseren, vend dem med sukker og 1–2 spsk. kartoffelmel eller maizena, og fordel dem straks på tærtebunden. Stivelsen jævner saften under bagningen og sikrer en sprød bund.`
      },
      {
        h2: "Holdbarhed på frost",
        body: `Ifølge Forbrugerrådet Tænks holdbarhedstabel holder frugt og frugtprodukter sig i 10–12 måneder i fryseren ved -18 °C.`
      }
    ],
    faq: [
      { q: "Skal rabarber blancheres før frysning?", a: "Nej, rabarber har et højt naturligt syreindhold, der konserverer stilkene, så de kan fryses rå i stykker uden blanchering." },
      { q: "Må man spise bladene på rabarber?", a: "Nej, rabarberblade indeholder giftige koncentrationer af oxalsyre og skal altid skæres af og smides ud." },
      { q: "Hvor længe kan frosne rabarber holde sig?", a: "Frugt og frugtprodukter kan holde sig i 10–12 måneder ved -18 °C ifølge Forbrugerrådet Tænk." },
      { q: "Skal frosne rabarber tøs op før bagning af kage?", a: "Nej, vend de frosne stykker med sukker og lidt kartoffelmel og kom dem direkte i kagen fra frost." }
    ],
    internalLinks: [
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Holdbarhed i fryseren for frugt og grønt", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Frysning af bær", href: "/baer" },
      { text: "Frysning af æbler og pærer", href: "/aebler-og-paerer" },
      { text: "Frysning af kager og tærter", href: "/kager" }
    ],
    sources: [
      SOURCES.taenkHoldbarhed,
      SOURCES.foedevarestyrelsenOpbevaring
    ]
  }
];
